import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, ChevronRight, ExternalLink, KeyRound, Lock, Trash2 } from 'lucide-react';
import { api } from '../../lib/api';
import { Button, ErrorNote, IconButton, Spinner } from '../../components/ui';

export interface SecretInfo { id: string; name: string; description: string; steps: string[]; getUrl: string | null; hasValue: boolean; allowedHosts: string[]; allowedRoutes: string[]; updatedAt: string }

export function useSecrets(artifactId: string) {
  const qc = useQueryClient();
  const key = ['secrets', artifactId];
  const q = useQuery({ queryKey: key, queryFn: () => api<{ secrets: SecretInfo[] }>(`/api/artifacts/${artifactId}/secrets`).then((r) => r.secrets) });
  const set = (s: SecretInfo[]) => qc.setQueryData(key, s);
  const save = useMutation({
    mutationFn: (b: { name: string; value?: string; description?: string; allowedHosts?: string[]; allowedRoutes?: string[] }) => {
      const { name, ...body } = b;
      return api<{ secrets: SecretInfo[] }>(`/api/artifacts/${artifactId}/secrets/${encodeURIComponent(name)}`, { method: 'PUT', body }).then((r) => r.secrets);
    },
    onSuccess: set,
  });
  const remove = useMutation({ mutationFn: (name: string) => api<{ secrets: SecretInfo[] }>(`/api/artifacts/${artifactId}/secrets/${encodeURIComponent(name)}`, { method: 'DELETE' }).then((r) => r.secrets), onSuccess: set });
  return { q, save, remove };
}

/** One secret: shows where it may be sent, and takes the value in a password field. The value is never shown again. */
function SecretRow({ s, onSave, saving, onDelete }: { s: SecretInfo; onSave: (value: string) => void; saving: boolean; onDelete?: () => void }) {
  const [value, setValue] = useState('');
  const [editing, setEditing] = useState(!s.hasValue);
  return (
    <div className="secret">
      <div className="secret__head">
        <KeyRound size={15} />
        <code>{s.name}</code>
        {s.hasValue ? <span className="tag tag--ok"><Check size={11} /> Saved</span> : <span className="tag">Needed</span>}
        <span style={{ flex: 1 }} />
        {s.hasValue && !editing && <Button size="sm" variant="ghost" onClick={() => setEditing(true)}>Replace</Button>}
        {onDelete && <IconButton size="sm" label={`Delete ${s.name}`} onClick={onDelete}><Trash2 size={14} /></IconButton>}
      </div>
      {s.description && <p className="muted small">{s.description}</p>}
      {s.steps.length > 0 && (
        <details className="secret__steps" open={!s.hasValue}>
          <summary><ChevronRight size={13} /> How to get {s.hasValue ? 'a new' : 'this'} key</summary>
          <ol>{s.steps.map((step, n) => <li key={n}>{step}</li>)}</ol>
          {s.getUrl && <a className="btn btn--secondary btn--sm" href={s.getUrl} target="_blank" rel="noopener noreferrer"><ExternalLink size={13} /><span>Open {(() => { try { return new URL(s.getUrl).hostname.replace(/^www\./, ''); } catch { return 'the site'; } })()}</span></a>}
        </details>
      )}
      <p className="secret__scope small">Only sent to <strong>{s.allowedHosts.join(', ')}</strong>{s.allowedRoutes.length ? <> · only for {s.allowedRoutes.join(', ')}</> : null}</p>
      {editing && (
        <form className="secret__form" onSubmit={(e) => { e.preventDefault(); if (value.trim()) { onSave(value); setValue(''); setEditing(false); } }}>
          <input type="password" autoComplete="off" spellCheck={false} placeholder={`Paste ${s.name}`} value={value} onChange={(e) => setValue(e.target.value)} aria-label={`Value for ${s.name}`} />
          <Button type="submit" size="sm" variant="primary" loading={saving} disabled={!value.trim()}>Save</Button>
        </form>
      )}
    </div>
  );
}

/** Secure fields for the secrets an app needs. Values go straight to encrypted storage, never into the chat. */
export function SecretsForm({ artifactId, only, manage }: { artifactId: string; only?: string[]; manage?: boolean }) {
  const { q, save, remove } = useSecrets(artifactId);
  const [adding, setAdding] = useState({ name: '', value: '', hosts: '', routes: '' });
  if (q.isLoading) return <Spinner />;
  if (!q.data) return <ErrorNote error={q.error} />;
  const list = only ? q.data.filter((s) => only.includes(s.name)) : q.data;
  return (
    <div className="secrets">
      <p className="secrets__note"><Lock size={13} /> Keys are encrypted and never shown in the chat, to the AI, or in your app&rsquo;s code. Your app uses them through Lumnos, only on the websites listed.</p>
      {list.map((s) => (
        <SecretRow key={s.name} s={s} saving={save.isPending && save.variables?.name === s.name} onSave={(value) => save.mutate({ name: s.name, value })} onDelete={manage ? () => { if (confirm(`Delete ${s.name}? Parts of the app that use it will stop working.`)) remove.mutate(s.name); } : undefined} />
      ))}
      {!list.length && <p className="muted small">No secrets yet.</p>}
      {manage && (
        <form className="secret secret--new" onSubmit={(e) => {
          e.preventDefault();
          save.mutate({ name: adding.name.trim().toUpperCase(), value: adding.value, allowedHosts: adding.hosts.split(/[\s,]+/).filter(Boolean), allowedRoutes: adding.routes.split(',').map((r) => r.trim()).filter(Boolean) }, { onSuccess: () => setAdding({ name: '', value: '', hosts: '', routes: '' }) });
        }}>
          <strong className="small">Add a secret</strong>
          <input placeholder="NAME (e.g. SERVICE_API_KEY)" value={adding.name} onChange={(e) => setAdding({ ...adding, name: e.target.value.toUpperCase().replace(/[^A-Z0-9_]/g, '_') })} aria-label="Secret name" />
          <input type="password" autoComplete="off" placeholder="Value" value={adding.value} onChange={(e) => setAdding({ ...adding, value: e.target.value })} aria-label="Secret value" />
          <input placeholder="Allowed API hostname (e.g. api.example.com)" value={adding.hosts} onChange={(e) => setAdding({ ...adding, hosts: e.target.value })} aria-label="Allowed websites" />
          <input placeholder="Optional: allowed request paths (e.g. POST /v1/items)" value={adding.routes} onChange={(e) => setAdding({ ...adding, routes: e.target.value })} aria-label="Allowed actions" />
          <Button type="submit" size="sm" variant="primary" loading={save.isPending && save.variables?.name === adding.name} disabled={!adding.name || !adding.value || !adding.hosts}>Add</Button>
        </form>
      )}
      <ErrorNote error={save.error ?? remove.error} />
    </div>
  );
}
